import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Image as ImageIcon, Loader2, X } from "lucide-react";
import { adminCreateSubscriptionTier, adminUpdateSubscriptionTier, adminUploadSubscriptionTierImage, AI_PROVIDER_OPTIONS } from "../../../lib/api";
import { TierImage } from "./TierImage";

export function TierEditorDialog({ open, onClose, tier, onSaved }) {
    const isEdit = !!tier;
    const [form, setForm] = useState({
        title: "",
        description: "",
        price_usd: 0,
        duration_days: 30,
        credit_amount: 0,
        revenuecat_product_id: "",
        revenuecat_entitlement_id: "",
        provider: "",
        sort_order: 0,
        active: true,
    });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const [localImagePath, setLocalImagePath] = useState("");

    useEffect(() => {
        if (!open) return;
        if (tier) {
            setForm({
                title: tier.title || "",
                description: tier.description || "",
                price_usd: Number(tier.price_usd) || 0,
                duration_days: Number(tier.duration_days) || 30,
                credit_amount: Number(tier.credit_amount) || 0,
                revenuecat_product_id: tier.revenuecat_product_id || "",
                revenuecat_entitlement_id: tier.revenuecat_entitlement_id || "",
                provider: tier.provider || "",
                sort_order: Number(tier.sort_order) || 0,
                active: !!tier.active,
            });
            setLocalImagePath(tier.image_storage_path || "");
        } else {
            setForm({
                title: "",
                description: "",
                price_usd: 0,
                duration_days: 30,
                credit_amount: 0,
                revenuecat_product_id: "",
                revenuecat_entitlement_id: "",
                provider: "",
                sort_order: 0,
                active: true,
            });
            setLocalImagePath("");
        }
    }, [open, tier]);

    if (!open) return null;

    const update = (patch) => setForm((s) => ({ ...s, ...patch }));

    const handleImage = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        let workingTier = tier;
        if (!workingTier) {
            // Need to create the tier first so we have an id to attach
            // the image to. Use whatever title we have.
            if (!form.title.trim()) {
                toast.error("Enter a title before uploading an image.");
                return;
            }
            try {
                workingTier = await adminCreateSubscriptionTier(form);
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Couldn't create tier.");
                return;
            }
        }
        setUploading(true);
        try {
            const resp = await adminUploadSubscriptionTierImage(
                workingTier.id,
                file,
            );
            setLocalImagePath(resp.storage_path);
            toast.success("Image uploaded");
            // If we had to create the tier early, surface that to the
            // parent so the list refreshes with the new row + image.
            if (!tier && workingTier) onSaved && onSaved(workingTier);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!form.title.trim()) {
            toast.error("Title is required.");
            return;
        }
        setSaving(true);
        try {
            let saved;
            if (isEdit) {
                saved = await adminUpdateSubscriptionTier(tier.id, form);
            } else {
                saved = await adminCreateSubscriptionTier(form);
            }
            toast.success(isEdit ? "Tier updated" : "Tier created");
            onSaved && onSaved(saved);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            data-testid="tier-editor-overlay"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl bg-[#0F1B3A] border border-[#2A3655] rounded-md shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="tier-editor-dialog"
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#2A3655] bg-[#0A1128]">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-white">
                        {isEdit ? "Edit subscription tier" : "New subscription tier"}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="tier-editor-close"
                        className="text-slate-400 hover:text-white"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                    {/* Image */}
                    <div className="flex items-start gap-4">
                        <TierImage storagePath={localImagePath} alt={form.title} />
                        <div className="flex-1">
                            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                                Tier image
                            </label>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                className="hidden"
                                onChange={handleImage}
                                data-testid="tier-image-input"
                            />
                            <div className="mt-1.5 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => fileRef.current?.click()}
                                    disabled={uploading}
                                    data-testid="tier-image-upload-btn"
                                    className="px-3 py-1.5 text-xs rounded-sm bg-[#1C253C] hover:bg-[#2A3655] border border-[#2A3655] text-white font-semibold disabled:opacity-50"
                                >
                                    {uploading ? (
                                        <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                                    ) : (
                                        <ImageIcon className="w-3 h-3 inline mr-1" />
                                    )}
                                    {localImagePath ? "Replace image" : "Upload image"}
                                </button>
                                <span className="text-[10px] text-slate-500">
                                    PNG/JPG/WebP/GIF · up to 5 MB
                                </span>
                            </div>
                        </div>
                    </div>

                    <Field
                        label="Title"
                        value={form.title}
                        onChange={(v) => update({ title: v })}
                        testid="tier-title"
                    />
                    <Field
                        label="Description"
                        value={form.description}
                        onChange={(v) => update({ description: v })}
                        multiline
                        testid="tier-description"
                    />
                    <div className="grid grid-cols-3 gap-3">
                        <Field
                            label="Price (USD)"
                            value={form.price_usd}
                            onChange={(v) => update({ price_usd: Number(v) || 0 })}
                            type="number"
                            testid="tier-price"
                        />
                        <Field
                            label="Duration (days)"
                            value={form.duration_days}
                            onChange={(v) => update({ duration_days: Number(v) || 30 })}
                            type="number"
                            testid="tier-duration"
                        />
                        <Field
                            label="Credits / period"
                            value={form.credit_amount}
                            onChange={(v) => update({ credit_amount: Number(v) || 0 })}
                            type="number"
                            testid="tier-credits"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field
                            label="RevenueCat product ID"
                            value={form.revenuecat_product_id}
                            onChange={(v) => update({ revenuecat_product_id: v })}
                            testid="tier-rc-product"
                            placeholder="e.g. rc_web_pro_monthly"
                        />
                        <Field
                            label="RevenueCat entitlement ID"
                            value={form.revenuecat_entitlement_id}
                            onChange={(v) => update({ revenuecat_entitlement_id: v })}
                            testid="tier-rc-entitlement"
                            placeholder="e.g. pro"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400 block mb-1">
                            AI provider for this tier
                        </label>
                        <select
                            value={form.provider}
                            onChange={(e) => update({ provider: e.target.value })}
                            data-testid="tier-provider"
                            className="w-full px-3 py-2 bg-[#1C253C] border border-[#2A3655] rounded-sm text-white text-sm outline-none focus:border-[#00A3FF]"
                        >
                            {AI_PROVIDER_OPTIONS.map((opt) => (
                                <option key={opt.id || "default"} value={opt.id}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <p className="text-[11px] text-slate-500 mt-1">
                            Subscribers on this tier get routed to the picked provider for every AI call.
                            Leave on <em>"Use global default"</em> to use whichever provider you've selected in Admin → Integrations.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field
                            label="Sort order"
                            value={form.sort_order}
                            onChange={(v) => update({ sort_order: Number(v) || 0 })}
                            type="number"
                            testid="tier-sort"
                        />
                        <div className="flex items-end pb-1.5">
                            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.active}
                                    onChange={(e) =>
                                        update({ active: e.target.checked })
                                    }
                                    data-testid="tier-active"
                                    className="w-4 h-4 rounded-sm"
                                />
                                Active (visible on subscribe page)
                            </label>
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#2A3655] bg-[#0A1128]">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="tier-editor-cancel"
                        className="px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm border border-[#2A3655] text-slate-300 hover:text-white hover:border-[#00A3FF]/60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="tier-editor-save"
                        className="px-4 py-1.5 text-xs uppercase tracking-wider font-bold rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white disabled:opacity-50"
                    >
                        {saving ? (
                            <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
                        ) : (
                            <Save className="w-3 h-3 inline mr-1" />
                        )}
                        {isEdit ? "Save" : "Create tier"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function Field({ label, value, onChange, type, multiline, placeholder, testid }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                {label}
            </span>
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testid}
                    rows={2}
                    className="w-full bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm px-3 py-1.5 text-sm text-white outline-none resize-y"
                />
            ) : (
                <input
                    type={type || "text"}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testid}
                    className="w-full bg-[#0A1128] border border-[#2A3655] focus:border-[#00A3FF] rounded-sm px-3 py-1.5 text-sm text-white outline-none"
                />
            )}
        </label>
    );
}

// ----- AI rates panel -----
